/**
 * CR-7352: who owns a collection.
 *
 * A collection with no named owner is everybody's job and therefore nobody's
 * — it sits in the register until someone notices it. These two pieces put a
 * name on it: a picker on the Initiate form, and a reassign control on a
 * collection already running. Both offer only the users the assign endpoint
 * will accept (active users in the NCR's own company), so the picker can
 * never appear to assign and silently fail.
 *
 * @link MyCollections for the queue the assignee then works from.
 */
import { useEffect, useState } from 'react';
import { Loader2, UserCheck } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { qualityAPI } from '../../lib/api';
import type { CollectionAssignee } from '../../lib/api';

/** No owner. SearchableSelect's own convention for "nothing selected". */
export const UNASSIGNED = '';

/**
 * Fetch the users this NCR's collection may be assigned to.
 *
 * @param ncrId - the NCR being viewed
 * @param enabled - false for suppliers and completed collections, where the
 *   list is neither shown nor permitted (the endpoint 403s a supplier)
 * @returns the assignees, whether they are still loading, and a load error
 */
export function useCollectionAssignees(ncrId: number, enabled: boolean) {
  const [assignees, setAssignees] = useState<CollectionAssignee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !ncrId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    qualityAPI
      .getCollectionAssignees(ncrId)
      .then((res) => {
        if (!cancelled) setAssignees(res.data ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Error loading collection assignees:', err);
        setError('Could not load the list of users to assign to.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ncrId, enabled]);

  return { assignees, loading, error };
}

interface AssigneeSelectProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  assignees: CollectionAssignee[];
  loading: boolean;
  error: string | null;
  disabled?: boolean;
}

/**
 * The picker itself.
 *
 * `SearchableSelect` rather than a plain Select because a customer company
 * has dozens of users — 41 on the DE tenant — and scrolling a list that long
 * to find one name is the wrong interaction. It types to filter, opens
 * downward under the field, and its placeholder row is the "unassigned"
 * choice, so clearing an owner needs no separate control.
 */
export function CollectionAssigneeSelect({
  id, value, onChange, assignees, loading, error, disabled,
}: AssigneeSelectProps) {
  if (error) {
    return <p className="text-sm text-red-500">{error}</p>;
  }
  return (
    <SearchableSelect
      id={id}
      options={assignees.map((user) => ({ value: String(user.id), label: user.name }))}
      value={value}
      onChange={onChange}
      placeholder={loading ? 'Loading users…' : 'Unassigned'}
      disabled={disabled || loading}
    />
  );
}

interface AssignmentPanelProps {
  ncrId: number;
  assignedTo: number | null;
  assignedToName?: string | null;
  /** Completed collections are read-only — there is nothing left to chase. */
  editable: boolean;
  saving: boolean;
  onAssign: (assignedTo: number | null) => void;
}

/**
 * The "Assigned to" block on a collection that already exists: who owns it
 * now, and — while it is still open — a control to hand it to someone else
 * or clear it.
 */
export function CollectionAssignmentPanel({
  ncrId, assignedTo, assignedToName, editable, saving, onAssign,
}: AssignmentPanelProps) {
  const { assignees, loading, error } = useCollectionAssignees(ncrId, editable);
  const [draft, setDraft] = useState<string>(assignedTo ? String(assignedTo) : UNASSIGNED);

  // Follow the server after a save, a refetch or a reassignment made
  // elsewhere; the draft is only ever ahead of it mid-edit.
  useEffect(() => {
    setDraft(assignedTo ? String(assignedTo) : UNASSIGNED);
  }, [assignedTo]);

  const changed = draft !== (assignedTo ? String(assignedTo) : UNASSIGNED);

  return (
    <div className="border-t pt-4 space-y-3">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <UserCheck className="h-4 w-4" />
        Assigned to
      </h4>
      <p className="text-sm">
        {assignedToName
          ? <span className="font-medium">{assignedToName}</span>
          : <span className="text-muted-foreground">Nobody — this collection has no owner.</span>}
      </p>
      {editable && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2 w-72 max-w-full">
            <Label htmlFor="collection-assignee">Change owner</Label>
            <CollectionAssigneeSelect
              id="collection-assignee"
              value={draft}
              onChange={setDraft}
              assignees={assignees}
              loading={loading}
              error={error}
              disabled={saving}
            />
          </div>
          <Button
            variant="outline"
            disabled={!changed || saving || loading}
            onClick={() => onAssign(draft === UNASSIGNED ? null : parseInt(draft, 10))}
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
            {draft === UNASSIGNED ? 'Clear owner' : 'Assign'}
          </Button>
        </div>
      )}
    </div>
  );
}
